import {
  Card,
  GhostButton,
  Icon,
  PrimaryButton,
  Text,
  useColors,
  DropdownMenu,
  MenuItem,
} from "@certn/cascadia-react";
import { GhostIconButton } from "@certn/cascadia-react/components/button/GhostIconButton";
import { CascadiaColor } from "@certn/cascadia-tokens";
import { useFutureTimeDifference, usePastTimeDifference } from "@certn/utils";
import { css } from "@emotion/css";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { CannotDeclineModal } from "./CannotDeclineModal";
import { DeclineModal } from "./DeclineModal";
import { ExpirationBar } from "./ExpirationBar";
import { useCardStyles } from "./styles";
import { CheckStatus } from "../check-status/CheckStatus";
import constants, { IS_LOCALHOST } from "../../../constants";

export type PortalCardProps = {
  title: string;
  type:
    | "ready"
    | "progress"
    | "upgrade"
    | "expired"
    | "review"
    | "information-request";
  subtitle?: React.ReactNode;
  details?: React.ReactNode;
  receivedDate: Date;
  expirationDate?: Date;
  applicantId: string;
  onPress: () => void;
  teamName?: string | null;
  disableDeclineCase?: boolean;
  checksOrdered: ReadonlyArray<{ type: string; status: string }>;
};

const cardVariant: Record<
  PortalCardProps["type"],
  {
    testId: string;
    background: CascadiaColor;
    statusText: React.ReactElement;
    actionBarBackground: CascadiaColor;
    actionBarIconColor: CascadiaColor;
    textColor: CascadiaColor;
    receivedText: React.ReactElement;
    button: (onPress: () => void) => React.ReactElement;
  }
> = {
  ready: {
    testId: "check-card-get-started-badge",
    background: "background-success-strong",
    statusText: <Trans>Ready to start</Trans>,
    actionBarBackground: "background-base",
    actionBarIconColor: "icon-success",
    textColor: "text-weak",
    receivedText: <Trans>Received</Trans>,
    button: (onPress) => (
      <PrimaryButton
        testID="dashboard-check-card-get-started-button"
        size="medium"
        fullWidth
        onPress={onPress}
      >
        <Trans>Get started</Trans>
      </PrimaryButton>
    ),
  },
  upgrade: {
    testId: "check-card-upgrade-badge",
    background: "background-success-strong",
    statusText: <Trans>Ready to start</Trans>,
    actionBarBackground: "background-base",
    actionBarIconColor: "icon-success",
    textColor: "text-weak",
    receivedText: <Trans>Follow up check requested</Trans>,
    button: (onPress) => (
      <PrimaryButton
        testID="dashboard-check-card-get-started-button"
        size="medium"
        fullWidth
        onPress={onPress}
      >
        <Trans>Get started</Trans>
      </PrimaryButton>
    ),
  },
  progress: {
    testId: "dashboard-check-card-in-progress-badge",
    background: "background-info-strong",
    statusText: <Trans>In progress</Trans>,
    actionBarBackground: "background-base",
    actionBarIconColor: "icon-info",
    textColor: "text-weak",
    receivedText: <Trans>Received</Trans>,
    button: (onPress) => (
      <PrimaryButton
        testID="check-card-continue-button"
        size="medium"
        variant="info"
        fullWidth
        onPress={onPress}
      >
        <Trans>Continue</Trans>
      </PrimaryButton>
    ),
  },
  expired: {
    testId: "dashboard-check-card-expired-badge",
    background: "background-inverse",
    statusText: <Trans>Expired</Trans>,
    actionBarBackground: "background-base",
    actionBarIconColor: "background-inverse",
    textColor: "text-weak",
    receivedText: <Trans>Received</Trans>,
    button: (onPress) => (
      <GhostButton
        border="border-strong"
        size="medium"
        fullWidth
        testID="dashboard-check-card-archive-button"
        onPress={onPress}
      >
        <Trans>Archive</Trans>
      </GhostButton>
    ),
  },
  review: {
    testId: "dashboard-check-card-review-badge",
    background: "background-info-strong",
    statusText: <Trans>Requires review</Trans>,
    actionBarBackground: "background-base",
    actionBarIconColor: "background-info-strong",
    textColor: "text-weak",
    receivedText: <Trans>Received</Trans>,
    button: (onPress) => (
      <PrimaryButton
        testID="dashboard-check-card-review-button"
        size="medium"
        variant="info"
        fullWidth
        onPress={onPress}
      >
        <Trans>Review my results</Trans>
      </PrimaryButton>
    ),
  },
  "information-request": {
    testId: "dashboard-information-request-badge",
    background: "background-warning-strong",
    statusText: <Trans>Action needed</Trans>,
    actionBarBackground: "background-base",
    actionBarIconColor: "icon-warning",
    textColor: "text-weak",
    receivedText: <Trans>Received</Trans>,
    button: (onPress) => (
      <PrimaryButton
        testID="dashboard-information-request-button"
        size="medium"
        variant="warning"
        fullWidth
        onPress={onPress}
      >
        <Trans>View request</Trans>
      </PrimaryButton>
    ),
  },
};

const cardTypesWithoutDecline: PortalCardProps["type"][] = [
  "expired",
  "information-request",
];

const checkTypeToLabel = (checkType: string): string => {
  const base = checkType.replace(/_\d+$/, ""); // drop trailing _1, _23, etc.
  return base
    .toLowerCase()
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

export function PortalCard({
  title,
  type,
  subtitle,
  details,
  onPress,
  receivedDate,
  expirationDate,
  applicantId,
  teamName,
  disableDeclineCase,
  checksOrdered,
}: PortalCardProps) {
  const styles = useCardStyles();
  const { t } = useTranslation();
  const { days: daysToExpire } = useFutureTimeDifference(
    expirationDate ?? new Date()
  );
  const receivedTimeAgo = usePastTimeDifference(receivedDate);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const colors = useColors();

  return (
    <>
      <Card
        type="large"
        border="border-base"
        background="background-accent-neutral-weak"
        className={styles.card}
      >
        {expirationDate && type !== "expired" && daysToExpire <= 7 && (
          <ExpirationBar expirationDate={expirationDate} />
        )}
        <div className={styles.statusDecline}>
          <CheckStatus type={type} testId={cardVariant[type].testId}>
            {cardVariant[type].statusText}
          </CheckStatus>
          {!cardTypesWithoutDecline.includes(type) && (
            <div className={css({ marginLeft: "auto" })}>
              <DropdownMenu
                testID="dashboard-expand-portal-card-menu"
                triggerButton={
                  <GhostIconButton
                    testID="dashboard-expand-portal-card-button"
                    size="small"
                    icon="more_horiz"
                    aria-label={t("Expand menu")}
                  />
                }
                onItemAction={(id) => {
                  switch (id) {
                    case "decline-request":
                      setShowDeclineModal(true);
                      break;
                  }
                }}
              >
                <MenuItem
                  textStyle="input/text-field"
                  testID="dashboard-decline-request-button"
                  id="decline-request"
                >
                  <Trans>Decline request</Trans>
                </MenuItem>
              </DropdownMenu>
            </div>
          )}
        </div>
        <Text
          as="h3"
          style="global/body-secondary"
          testID="applicant-type-card"
        >
          {subtitle ?? <Trans>Background check application</Trans>}
        </Text>
        <Text style="card/title-large" testID="company-name-card">
          {title}
        </Text>
        {details ? (
          <Text style="card/title-medium" testID="card-details">
            {details}
          </Text>
        ) : (
          (constants.MODE === "develop" || IS_LOCALHOST) && (
            <Text style="card/title-medium" testID="checks-ordered">
              {checksOrdered
                .map((check) => checkTypeToLabel(check.type))
                .join(", ")}
            </Text>
          )
        )}
        <div
          className={css(
            styles.actionBar,
            css({ background: colors[cardVariant[type].actionBarBackground] })
          )}
        >
          <div className={styles.actionBarText} data-testid="date-text-card">
            <Icon
              name="inbox"
              color={cardVariant[type].actionBarIconColor}
              type="standard"
              padding="space-1-5"
            />
            <Text style="input/label" color={cardVariant[type].textColor}>
              {cardVariant[type].receivedText} {receivedTimeAgo}
            </Text>
          </div>
          <div className={styles.buttonContainer}>
            {cardVariant[type].button(onPress)}
          </div>
        </div>
        <div
          className={css(
            styles.background,
            css({
              background: colors[cardVariant[type].background],
            })
          )}
        />
        <div className={styles.circles}>
          <span className={css(styles.circle, styles.c1)} />
          <span className={css(styles.circle, styles.c2)} />
          <span className={css(styles.circle, styles.c3)} />
          <span className={css(styles.circle, styles.c4)} />
          <span className={css(styles.circle, styles.c5)} />
          <span className={css(styles.circle, styles.c6)} />
        </div>
      </Card>
      {disableDeclineCase ? (
        <CannotDeclineModal
          show={showDeclineModal}
          onClose={() => setShowDeclineModal(false)}
        />
      ) : (
        <DeclineModal
          show={showDeclineModal}
          onClose={() => setShowDeclineModal(false)}
          applicantId={applicantId}
          teamName={teamName}
        />
      )}
    </>
  );
}
